import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Commonservice } from '../../../services/commonservice';
import {
  buildPaginationItems,
  buildPaginationLabel,
  buildQueryString,
  buildTimestampFileName,
  canShowPagination,
  downloadBlob,
  extractPagination,
  getApiErrorMessage,
} from '../../../helper/table-utils';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  ManageScadaCodeFormValue,
  ManageScadaCodeItem,
  ManageScadaCodePagination,
} from './manage-scada-codes.interfaces';

const SCADA_CODE_ALL_FILTER_VALUE = 'all';
type ManageScadaCodeSortField = 'name' | 'code';
type ManageScadaCodeSortOrder = 'asc' | 'desc';
const SCADA_CODE_NO_RECORDS_LABEL = 'No records';
const SCADA_CODE_SHOWING_RESULTS_LABEL = 'Showing';
const SCADA_CODE_RESULTS_LABEL = 'results';
const SCADA_CODE_FAILED_LOAD_MESSAGE = 'Failed to load scada codes:';
const SCADA_CODE_FAILED_SAVE_MESSAGE = 'Failed to update scada code.';
const SCADA_CODE_FAILED_DELETE_MESSAGE = 'Failed to delete scada code.';
const SCADA_CODE_FAILED_EXPORT_MESSAGE = 'Failed to export scada codes.';
const SCADA_CODE_CODE_TYPE_OPTIONS = [
  { value: SCADA_CODE_ALL_FILTER_VALUE, label: 'All Types' },
  { value: 'generator', label: 'Generator' },
  { value: 'substation', label: 'Substation' },
  { value: 'transmission_line', label: 'Transmission Line' },
];

type ManageScadaCodeQueryValue = string | number | null | undefined;
type ManageScadaCodeQueryParams = Record<string, ManageScadaCodeQueryValue>;

interface ManageScadaCodeFilterOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-manage-scada-codes',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-scada-codes.html',
  styleUrl: './manage-scada-codes.scss',
})
export class ManageScadaCodesPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly codeTypeOptions: ManageScadaCodeFilterOption[] = SCADA_CODE_CODE_TYPE_OPTIONS;

  protected readonly filterForm: UntypedFormGroup;
  protected readonly scadaCodeForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isSaving = false;
  protected isDeleting = false;
  protected selectedScadaCode: ManageScadaCodeItem | null = null;
  protected editModalOpen = false;
  protected deleteModalOpen = false;
  protected formSubmitted = false;
  protected sortBy: ManageScadaCodeSortField | null = null;
  protected sortOrder: ManageScadaCodeSortOrder = 'asc';
  protected modalMode: 'edit' | 'view' = 'edit';

  protected scadaCodes: ManageScadaCodeItem[] = [];
  protected filteredScadaCodes: ManageScadaCodeItem[] = [];
  protected pagination: ManageScadaCodePagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      code_type: [SCADA_CODE_ALL_FILTER_VALUE],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.scadaCodeForm = this.fb.group({
      ele_id: [null, [Validators.required]],
      code_type: ['', [Validators.required, Validators.maxLength(50)]],
      project_unique_code: [{ value: null, disabled: true }],
      code: ['', [Validators.required, Validators.maxLength(50)]],
    });
  }

  ngOnInit(): void {
    this.loadScadaCodes(1);

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadScadaCodes(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        code_type: SCADA_CODE_ALL_FILTER_VALUE,
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadScadaCodes(1);
  }

  protected exportScadaCodes(): void {
    if (this.isLoading || this.isExporting || this.isSaving || this.isDeleting) {
      return;
    }

    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      code_type: this.filterForm.get('code_type')?.value,
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_SCADA_CODES, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildScadaCodesFileName()),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || SCADA_CODE_FAILED_EXPORT_MESSAGE),
      });
  }

  protected loadScadaCodes(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      code_type: this.filterForm.get('code_type')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_SCADA_CODES_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
      next: (res) => {
        this.scadaCodes = this.mapScadaCodeList(res?.value?.scada_codes ?? res?.scada_codes ?? []);
        this.filteredScadaCodes = [...this.scadaCodes];
        this.pagination = this.extractPagination(res?.value) ?? this.extractPagination(res) ?? this.pagination;
        },
        error: (err) => {
          console.error(SCADA_CODE_FAILED_LOAD_MESSAGE, err);
          this.scadaCodes = [];
          this.filteredScadaCodes = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected openEditModal(item: ManageScadaCodeItem): void {
    this.modalMode = 'edit';
    this.selectedScadaCode = item;
    this.scadaCodeForm.enable({ emitEvent: false });
    this.scadaCodeForm.reset({
      ele_id: item.ele_id,
      code_type: item.code_type,
      project_unique_code: item.project_unique_code,
      code: item.code,
    });
    this.formSubmitted = false;
    this.editModalOpen = true;
  }

  protected openViewModal(item: ManageScadaCodeItem): void {
    this.modalMode = 'view';
    this.selectedScadaCode = item;
    this.scadaCodeForm.enable({ emitEvent: false });
    this.scadaCodeForm.reset({
      ele_id: item.ele_id,
      code_type: item.code_type,
      project_unique_code: item.project_unique_code,
      code: item.code,
    });
    this.formSubmitted = false;
    this.editModalOpen = true;
    this.scadaCodeForm.disable({ emitEvent: false });
  }

  protected closeEditModal(): void {
    this.editModalOpen = false;
    this.selectedScadaCode = null;
    this.modalMode = 'edit';
    this.scadaCodeForm.enable({ emitEvent: false });
    this.formSubmitted = false;
    this.scadaCodeForm.reset({
      ele_id: null,
      code_type: '',
      project_unique_code: null,
      code: '',
    });
  }

  protected openDeleteModal(item: ManageScadaCodeItem): void {
    this.selectedScadaCode = item;
    this.deleteModalOpen = true;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedScadaCode = null;
  }

  protected saveScadaCode(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.scadaCodeForm.invalid) {
      this.formSubmitted = true;
      this.scadaCodeForm.markAllAsTouched();
      return;
    }

    if (!this.selectedScadaCode) {
      return;
    }

    const payload = this.buildEditPayload();
    this.isSaving = true;

    this.httpSvc
      .requestCall(AuthEndPoints.EDIT_SCADA_CODE, ApiMethod.PATCH, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.commonSvc.showSuccessToastr('Scada code updated successfully');
          this.closeEditModal();
          this.loadScadaCodes(this.pagination.page_no);
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || SCADA_CODE_FAILED_SAVE_MESSAGE);
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedScadaCode) {
      return;
    }

    this.isDeleting = true;
    const payload = { id: this.selectedScadaCode.id };

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_SCADA_CODE, ApiMethod.DELETE, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDeleting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.commonSvc.showSuccessToastr('Scada code deleted successfully');
          this.closeDeleteModal();
          this.loadScadaCodes(this.pagination.page_no);
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || SCADA_CODE_FAILED_DELETE_MESSAGE);
        },
      });
  }

  protected trackByScadaCodeId(_index: number, item: ManageScadaCodeItem): number {
    return item.id;
  }

  protected toggleSort(field: ManageScadaCodeSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadScadaCodes(1);
  }

  protected isSortAscending(field: ManageScadaCodeSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageScadaCodeSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.scadaCodeForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(this.pagination, SCADA_CODE_NO_RECORDS_LABEL, SCADA_CODE_SHOWING_RESULTS_LABEL, SCADA_CODE_RESULTS_LABEL);
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadScadaCodes(pageNo);
  }

  protected formatCodeType(codeType: string | null | undefined): string {
    const normalized = typeof codeType === 'string' ? codeType.trim() : '';
    if (!normalized) {
      return '-';
    }
    return normalized
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private buildQueryString(params: ManageScadaCodeQueryParams): string {
    return buildQueryString(params, [SCADA_CODE_ALL_FILTER_VALUE]);
  }

  private buildScadaCodesFileName(): string {
    return buildTimestampFileName('scada_codes_export');
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadBlob(blob, fileName);
  }

  private extractPagination(value: unknown): ManageScadaCodePagination | null {
    return extractPagination<ManageScadaCodePagination>(value);
  }

  private mapScadaCodeList(items: unknown): ManageScadaCodeItem[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) => {
      const record = item as Record<string, unknown>;
        return {
          id: Number(record['id'] ?? 0),
          serial_no: Number(record['serial_no'] ?? 0),
          ele_id: Number(record['ele_id'] ?? 0),
          name: typeof record['name'] === 'string' ? record['name'] : '-',
          code_type: typeof record['code_type'] === 'string' ? record['code_type'] : '',
          project_unique_code: typeof record['project_unique_code'] === 'string' ? record['project_unique_code'] : null,
          code: typeof record['code'] === 'string' ? record['code'] : '',
        };
      });
  }

  private buildEditPayload(): Partial<ManageScadaCodeFormValue> & { id?: number } {
    const formValue = this.scadaCodeForm.getRawValue() as ManageScadaCodeFormValue;
    const payload: Partial<ManageScadaCodeFormValue> & { id?: number } = {
      ele_id: Number(formValue.ele_id),
      code_type: formValue.code_type,
      code: formValue.code,
    };

    if (this.selectedScadaCode) {
      payload.id = this.selectedScadaCode.id;
    }

    return payload;
  }

  private getApiErrorMessage(error: unknown): string | null {
    return getApiErrorMessage(error);
  }
}
